import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@store/appStore';
import { useAuthStore } from '@store/authStore';
import { sendQuoteToClient, notifyClientOfQuoteUpdate } from '../services/quoteService';
import { apiClient } from '@/lib/api';
import { formatDateToISO, formatDateToISOWithTimezone } from '@/utils';
import { ROUTES } from '../../../constants';
import { getAirportTimezone } from '@utils/airportTimezone';
import { toast } from "sonner";
import { trunc2 } from '@/utils';

/**
 * Persist every buffered aircraft custom_profile edit from the pricing
 * drawer. For each aircraft in the buffer:
 *   1. Upload any pending photos to S3, swapping blob previewUrls in
 *      image_urls[] for the real URLs.
 *   2. POST /organizations/:orgId/aircrafts/:aircraftId/custom-profile
 *      (201 create / 200 update), OR
 *   3. DELETE the same URL when the buffer entry is `null` (broker
 *      reverted to base).
 *
 * S3 key: `photos/{TAIL}/{timestamp}-{i}.{ext}`. Timestamp per save
 * guarantees no overwrite across retries.
 *
 * Returns true when any profile was written or deleted — the caller
 * uses this to decide whether to broad-invalidate React Query.
 */
const persistAircraftProfiles = async (aircraftProfiles, orgId) => {
  if (!aircraftProfiles || typeof aircraftProfiles !== 'object' || !orgId) return false;
  const entries = Object.entries(aircraftProfiles);
  if (entries.length === 0) return false;

  const extFromMime = (type) =>
    type === 'image/png' ? 'png'
    : type === 'image/jpeg' || type === 'image/jpg' ? 'jpg'
    : (String(type || '').split('/')[1] || 'jpg');

  let didWrite = false;
  for (const [aircraftId, profile] of entries) {
    if (!aircraftId) continue;

    // Revert-to-base — DELETE the persisted profile if it exists.
    if (profile === null) {
      try {
        await apiClient.deleteCustomAircraftProfile(orgId, aircraftId);
        didWrite = true;
      } catch (err) {
        // 404 is fine — nothing to delete. ApiError exposes the HTTP
        // code as `statusCode`; check `.status` too for defense.
        if (err?.statusCode !== 404 && err?.status !== 404) throw err;
      }
      continue;
    }
    if (!profile || typeof profile !== 'object') continue;

    // 1) Upload any pending photos, then swap blob previewUrls for real
    //    S3 URLs in image_urls[] (order preserved). New key layout
    //    embeds org_id so a later delete-guard can attribute ownership.
    const pending = Array.isArray(profile._pendingUploads) ? profile._pendingUploads : [];
    let imageUrls = Array.isArray(profile.image_urls) ? [...profile.image_urls] : [];
    if (pending.length > 0) {
      const tailFolder = String(profile.tail_number || aircraftId || 'unknown')
        .replace(/[^A-Z0-9]/gi, '')
        .toUpperCase() || 'UNKNOWN';
      const stamp = Date.now();
      const uploaded = await Promise.all(
        pending.map(async (p, i) => {
          const ext = extFromMime(p.file?.type);
          // photos/{org_id}/{TAIL}/{stamp}-{i}.{ext} — see
          // isOurOrgS3Photo (manual-aircraft/useAddAircraftSaveMachine.js)
          // for the mirror-image delete-guard rule.
          const key = `photos/${orgId}/${tailFolder}/${stamp}-${i}.${ext}`;
          const s3Urls = await apiClient.getS3SignedUrl(key, p.file?.type);
          const realUrl = await apiClient.uploadFileToS3(p.file, s3Urls);
          return { previewUrl: p.previewUrl, realUrl };
        }),
      );
      const swap = new Map(uploaded.map(({ previewUrl, realUrl }) => [previewUrl, realUrl]));
      imageUrls = imageUrls.map((u) => swap.get(u) || u).filter(Boolean);
      pending.forEach((p) => p?.previewUrl && URL.revokeObjectURL(p.previewUrl));
    }

    // 2) Strip transient sidecars from the buffered profile.
    //    Historical owned-aircraft sidecars (_aircraftOrgId,
    //    _aircraftExtraAttributes, _hasAmenitiesRow) are stripped
    //    tolerantly for compatibility with buffers built against the
    //    older two-branch flow — they're no longer produced by
    //    buildProfileBuffer but might live on in an in-flight session.
    const {
      _pendingUploads: _droppedPending,
      _originalImageUrls: originalImageUrls,
      _aircraftOrgId: _droppedAircraftOrgId,
      _aircraftExtraAttributes: _droppedAircraftExtras,
      _hasAmenitiesRow: _droppedHasAmenitiesRow,
      ...rest
    } = profile;

    // 3) Single write path — always upsert the org's `custom_profile`
    //    for this aircraft. This is safe for owned AND platform-catalog
    //    aircraft: custom_profile is a per-quote/per-org override layer,
    //    and never mutates the base `aircraft`/`aircraft_amenities`
    //    rows. Owned aircraft used to PATCH the base record + its
    //    amenities row from here — that poisoned every other view of
    //    the same tail (a broker's per-quote edit leaked into the
    //    canonical shape). getEffectiveAircraft on the read side
    //    already merges custom_profile → the displayed aircraft, and
    //    now synthesizes structured has_* booleans from the amenity
    //    list so a removed amenity here reads as false downstream.
    const body = { ...rest };
    if (imageUrls.length > 0 || Array.isArray(profile.image_urls)) {
      body.image_urls = imageUrls;
      body.banner_image_url = imageUrls[0] || null;
    }
    await apiClient.upsertCustomAircraftProfile(orgId, aircraftId, body);
    didWrite = true;

    // 3) Best-effort S3 cleanup — diff original \ final; any removed
    //    URL that lives under photos/{our-org-id}/… is safe to delete.
    //    Pre-migration keys (photos/{TAIL}/… without an org segment)
    //    fail the guard and are left alone by design.
    //    TODO(BE)-S3-DELETE — uncomment the actual delete once the BE
    //    delete-by-key endpoint (see api.js:deleteS3Object) ships.
    if (Array.isArray(originalImageUrls) && originalImageUrls.length > 0) {
      const finalSet = new Set(imageUrls);
      const removed = originalImageUrls.filter((u) => !finalSet.has(u));
      const orgSegment = `/photos/${orgId}/`;
      const toDelete = removed.filter((u) => typeof u === 'string' && u.includes(orgSegment));
      const skipped = removed.length - toDelete.length;
      if (toDelete.length > 0) {
        // eslint-disable-next-line no-console
        console.log(`[persistAircraftProfiles] ${toDelete.length} removed S3 photo(s) queued for delete, ${skipped} skipped as unknown-owner`, toDelete);
        // await Promise.all(
        //   toDelete.map(async (url) => {
        //     const key = url.slice(url.indexOf('/photos/') + 1);
        //     try { await apiClient.deleteS3Object(key); }
        //     catch (err) { console.warn('[persistAircraftProfiles] S3 delete failed (best-effort)', { key, err }); }
        //   }),
        // );
      } else if (skipped > 0) {
        // eslint-disable-next-line no-console
        console.log(`[persistAircraftProfiles] ${skipped} removed URL(s) skipped as unknown-owner (pre-migration or other org)`);
      }
    }
  }
  return didWrite;
};

/**
 * Build leg itinerary array from flights
 */
const buildLegItinerary = (flights) =>
  (flights || []).map((flight) => ({
    pax: flight.passengerCount || 1,
    departure_date: formatDateToISOWithTimezone(flight.departureDate, flight.departureTime, getAirportTimezone(flight.departure?.icao)) || null,
    arrival_address: null,
    arrival_airport: flight.arrival?.icao || "",
    departure_address: null,
    departure_airport: flight.departure?.icao || "",
    notes: flight.notes || "",
  }));

/**
 * Extract client name from quote data
 */
const getClientName = (clientData) =>
  [clientData?.firstName || clientData?.first_name, clientData?.lastName || clientData?.last_name]
    .filter(Boolean).join(' ') || "";

/**
 * Build bid payload from quote data
 */
const buildBidPayload = (quoteData, { finalPrice, createdBy, brokerOrgId }) => ({
  trip_number: `T${Math.floor(Math.random() * 1000000)}`,
  bid_number: `Q${Date.now()}`,
  created_by: createdBy,
  broker_id: createdBy,
  broker_org_id: brokerOrgId,
  client_name: getClientName(quoteData.client?.clientData),
  client_email: quoteData.client?.clientData?.email || "",
  client_phone: quoteData.client?.clientData?.phone || "",
  departure_airport: quoteData.flights?.[0]?.departure?.icao || "",
  arrival_airport: quoteData.flights?.[0]?.arrival?.icao || "",
  departure_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.departureDate, quoteData.flights?.[0]?.departureTime, getAirportTimezone(quoteData.flights?.[0]?.departure?.icao)) || null,
  return_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.returnDate, quoteData.flights?.[0]?.returnTime, getAirportTimezone(quoteData.flights?.[0]?.departure?.icao)) || null,
  passenger_count: quoteData.flights?.[0]?.passengerCount || 1,
  baggage_requirements: quoteData.flights?.[0]?.notes || "",
  special_requests: quoteData.settings?.notes || "",
  status: "DRAFT",
  total_amount: trunc2(parseFloat(finalPrice) || 0),
  currency: "USD",
  valid_until: quoteData.settings?.validUntil ? formatDateToISO(quoteData.settings.validUntil) : null,
  sent_at: null,
  viewed_at: null,
  accepted_at: null,
  notes: quoteData.settings?.notes || "",
  leg_itinerary: buildLegItinerary(quoteData.flights),
});

/**
 * Build a CLEAN quote payload for the nested-write endpoint's `quote`
 * sub-object. Returns the exact whitelist the API accepts. Spreading
 * `...quoteData` at this layer would send form-only structures (client
 * object, Date objects in `flights`, a `contractFile` File in `settings`,
 * `_editing`, etc.) which the backend rejects with HTTP 500.
 */
const buildQuoteCreatePayload = (quoteData, { brokerOrgId, createdBy, bidItems, status, settings }) => {
  const airportZone = getAirportTimezone(quoteData.flights?.[0]?.departure?.icao);
  return {
    client_id: quoteData.client?.clientData?.client_id || quoteData.client?.clientData?.id || null,
    departure_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.departureDate, quoteData.flights?.[0]?.departureTime, airportZone) || null,
    return_date: resolveReturnDate(quoteData),
    passenger_count: quoteData.flights?.[0]?.passengerCount || 1,
    // quote_number omitted — BE assigns it server-side. Aligned with
    // the send flow (quoteService.js commit 83e572b) and the duplicate
    // flow. BE accepts POST /quotes without quote_number and fills it in.
    departure_airport: quoteData.flights?.[0]?.departure?.icao || "",
    arrival_airport: quoteData.flights?.[0]?.arrival?.icao || "",
    is_multi_leg: quoteData.flightType === "multi-leg",
    is_round_trip: quoteData.flightType === "round-trip",
    broker_org_id: brokerOrgId,
    created_by: createdBy,
    status,
    bid_items: bidItems,
    total_due: settings?.final_price || 0,
    broker_margin: settings?.broker_margin || 0,
    platform_fee: settings?.platform_fee || 0,
    note_to_client: settings?.notes || "",
    federal_excise_tax: settings?.federal_excise_tax || 0,
    operator_cost: settings?.operator_cost || 0,
    taxable_amount: settings?.taxable_amount || 0,
    segment_tax: settings?.segment_tax || 0,
    international_tax: settings?.international_tax || 0,
    // Mirror of bid.valid_until so the quote row carries its own expiration.
    expires_at: settings?.validUntil ? formatDateToISO(settings.validUntil) : null,
  };
};

/**
 * Build bid update payload for existing trip group bids
 */
const buildBidUpdatePayload = (quoteData, finalPrice) => ({
  client_name: getClientName(quoteData.client?.clientData),
  client_email: quoteData.client?.clientData?.email || "",
  client_phone: quoteData.client?.clientData?.phone || "",
  departure_airport: quoteData.flights?.[0]?.departure?.icao || "",
  arrival_airport: quoteData.flights?.[0]?.arrival?.icao || "",
  departure_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.departureDate, quoteData.flights?.[0]?.departureTime, getAirportTimezone(quoteData.flights?.[0]?.departure?.icao)) || null,
  return_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.returnDate, quoteData.flights?.[0]?.returnTime, getAirportTimezone(quoteData.flights?.[0]?.departure?.icao)) || null,
  passenger_count: quoteData.flights?.[0]?.passengerCount || 1,
  baggage_requirements: quoteData.flights?.[0]?.notes || "",
  special_requests: quoteData.settings?.notes || "",
  status: "SENT",
  total_amount: trunc2(finalPrice || 0),
  currency: "USD",
  valid_until: quoteData.settings?.validUntil ? formatDateToISO(quoteData.settings.validUntil) : null,
  sent_at: null,
  viewed_at: null,
  accepted_at: null,
  notes: quoteData.settings?.notes || "",
  leg_itinerary: buildLegItinerary(quoteData.flights),
});

/**
 * `bid_options.aircraft_features` stores a scoped {beds, wifi, entertainment}
 * shape. When the FE receives an option hydrated via the API's `include=`
 * join, the field carries the full platform-catalog aircraft_features row
 * (aircraft_feature_id, tier, has_wingman, no_aog, custom_features,
 * created_at, updated_at, …) which the BE's insert 500s on.
 * This picks just the three keys the column expects, filling sensible
 * defaults when a source is missing them.
 */
const AIRCRAFT_FEATURES_DEFAULT = { beds: 1, wifi: false, entertainment: false };
const normalizeAircraftFeatures = (features) => {
  if (!features || typeof features !== 'object') return { ...AIRCRAFT_FEATURES_DEFAULT };
  return {
    beds: Number.isFinite(features.beds) ? features.beds : AIRCRAFT_FEATURES_DEFAULT.beds,
    wifi: typeof features.wifi === 'boolean' ? features.wifi : AIRCRAFT_FEATURES_DEFAULT.wifi,
    entertainment: typeof features.entertainment === 'boolean' ? features.entertainment : AIRCRAFT_FEATURES_DEFAULT.entertainment,
  };
};

// ─── Edit-mode helpers ───────────────────────────────────────────────────────

/**
 * Resolve the top-level `return_date` column from the form's flight data.
 *  - 1-leg round-trip shape: read flights[0].returnDate.
 *  - 2-leg round-trip shape: fall back to flights[1].departureDate.
 *  - One-way / multi-leg: null.
 * Mirrors the fallback already in `buildQuotePayload`.
 */
const resolveReturnDate = (quoteData) => {
  const zone = getAirportTimezone(quoteData.flights?.[0]?.departure?.icao);
  const fromFirstLeg = formatDateToISOWithTimezone(
    quoteData.flights?.[0]?.returnDate,
    quoteData.flights?.[0]?.returnTime,
    zone,
  );
  if (fromFirstLeg) return fromFirstLeg;
  if (quoteData.flightType === 'round-trip') {
    return formatDateToISOWithTimezone(
      quoteData.flights?.[1]?.departureDate,
      quoteData.flights?.[1]?.departureTime,
      zone,
    ) || null;
  }
  return null;
};

/**
 * Build a PATCH payload for the underlying bid in edit mode. Mirrors
 * `buildBidUpdatePayload` but DOES NOT flip status — edit must preserve
 * whatever status the quote is currently in (DRAFT/SENT/VIEWED/SIGNED).
 */
const buildBidEditPayload = (quoteData) => {
  const finalPrice = quoteData.settings?.final_price || 0;
  return {
    client_name: getClientName(quoteData.client?.clientData),
    client_email: quoteData.client?.clientData?.email || "",
    client_phone: quoteData.client?.clientData?.phone || "",
    departure_airport: quoteData.flights?.[0]?.departure?.icao || "",
    arrival_airport: quoteData.flights?.[0]?.arrival?.icao || "",
    departure_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.departureDate, quoteData.flights?.[0]?.departureTime, getAirportTimezone(quoteData.flights?.[0]?.departure?.icao)) || null,
    return_date: resolveReturnDate(quoteData),
    passenger_count: quoteData.flights?.[0]?.passengerCount || 1,
    baggage_requirements: quoteData.flights?.[0]?.notes || "",
    special_requests: quoteData.settings?.notes || "",
    total_amount: trunc2(parseFloat(finalPrice) || 0),
    currency: "USD",
    valid_until: quoteData.settings?.validUntil ? formatDateToISO(quoteData.settings.validUntil) : null,
    notes: quoteData.settings?.notes || "",
    leg_itinerary: buildLegItinerary(quoteData.flights),
  };
};

/**
 * Build a PATCH payload for the quote itself. Top-level fields only,
 * deliberately excluding identifiers (quote_id, quote_number, created_by,
 * broker_org_id, bid_items) and status — status is preserved on edit.
 */
const buildQuotePatchPayload = (quoteData) => {
  const airportZone = getAirportTimezone(quoteData.flights?.[0]?.departure?.icao);
  const settings = quoteData.settings || {};
  return {
    client_id: quoteData.client?.clientData?.client_id || quoteData.client?.clientData?.id || undefined,
    departure_airport: quoteData.flights?.[0]?.departure?.icao || "",
    arrival_airport: quoteData.flights?.[0]?.arrival?.icao || "",
    departure_date: formatDateToISOWithTimezone(quoteData.flights?.[0]?.departureDate, quoteData.flights?.[0]?.departureTime, airportZone) || null,
    return_date: resolveReturnDate(quoteData),
    passenger_count: quoteData.flights?.[0]?.passengerCount || 1,
    is_round_trip: quoteData.flightType === 'round-trip',
    is_multi_leg: quoteData.flightType === 'multi-leg',
    note_to_client: settings.notes || "",
    total_due: settings.final_price || 0,
    broker_margin: settings.broker_margin || 0,
    platform_fee: settings.platform_fee || 0,
    federal_excise_tax: settings.federal_excise_tax || 0,
    segment_tax: settings.segment_tax || 0,
    international_tax: settings.international_tax || 0,
    operator_cost: settings.operator_cost || 0,
    taxable_amount: settings.taxable_amount || 0,
    // Mirror of bid.valid_until so a plain quote-level query surfaces the expiration.
    expires_at: settings.validUntil ? formatDateToISO(settings.validUntil) : null,
  };
};

/**
 * Build the body for a single bid_option PATCH (or POST when new).
 *
 * Explicit whitelist — the hydrated form's option includes joined records
 * (`aircraft`, `operator`) and read-only columns (`created_at`, `updated_at`,
 * `bid_option_id`) that the backend rejects with HTTP 500 if forwarded.
 * Mirrors the field set used by `createBidOptions` in the create flow.
 */
const buildBidOptionPayload = (option = {}, bidId) => ({
  bid_id: bidId,
  aircraft_id: option.aircraft_id || '',
  operator_id: option.operator_id || '',
  base_price:        Number(option.base_price        || 0).toFixed(2),
  fuel_cost:         Number(option.fuel_cost         || 0).toFixed(2),
  landing_fees:      Number(option.landing_fees      || 0).toFixed(2),
  crew_fees:         Number(option.crew_fees         || 0).toFixed(2),
  catering_cost:     Number(option.catering_cost     || 0).toFixed(2),
  ground_transport:  Number(option.ground_transport  || 0).toFixed(2),
  other_fees:        Number(option.other_fees        || 0).toFixed(2),
  total_price:       Number(option.total_price       || 0).toFixed(2),
  broker_markup:     Number(option.broker_markup     || 0).toFixed(2),
  final_price:       Number(option.final_price       || 0).toFixed(2),
  federal_excise_tax: Number(option.federal_excise_tax || 0).toFixed(2),
  segment_tax:       Number(option.segment_tax       || 0).toFixed(2),
  // Trip-type flags + International Facilities Tax that the pricing
  // drawer writes into local formData. Without these, the drawer's
  // save is lossy on persist — the total price / final price bake in
  // the tax (so the number looks right on reload) but the checkbox
  // state and the breakdown row disappear on the next open.
  is_domestic:       option.is_domestic !== false,
  is_international:  !!option.is_international,
  international_tax: Number(option.international_tax || 0).toFixed(2),
  platform_fee:      Number(option.platform_fee      || 0).toFixed(2),
  operator_cost:     Number(option.operator_cost     || 0).toFixed(2),
  is_recommended:    !!option.is_recommended,
  recommendation_reason: option.recommendation_reason || '',
  availability_confirmed: option.availability_confirmed !== false,
  shortlisted:       option.shortlisted !== false,
  risk_level:        option.risk_level || 'LOW',
  trust_score:       Number(option.trust_score || 0).toFixed(2),
  estimated_flight_time: option.estimated_flight_time || 0,
  // The `bid_options.aircraft_features` column stores a scoped
  // {beds, wifi, entertainment} shape — NOT the joined platform-catalog
  // aircraft_features row (which includes aircraft_feature_id, tier,
  // custom_features, created_at, updated_at, etc.). When an option is
  // hydrated from the API's `include=` join, `option.aircraft_features`
  // carries that rich shape and the BE 500s on insert. Normalize here
  // so both PATCH and POST always send the simple shape.
  aircraft_features: normalizeAircraftFeatures(option.aircraft_features),
});

/**
 * Build a single bid_option entry for the nested-write endpoint's
 * `bid.bid_options` array. Same field whitelist as `buildBidOptionPayload`
 * but WITHOUT `bid_id` — the endpoint sets it from the enclosing bid — and
 * with an option to strip the `bid_option_id`:
 *   - INSERT semantics (option has no persisted row on this bid yet, or is
 *     `_isNew`): pass `stripId: true` so the endpoint INSERTS.
 *   - PATCH semantics (option is an already-persisted row on this bid, e.g.
 *     trip-group hand-off): keep the id so the endpoint PATCHes.
 */
const buildBidOptionPayloadForNestedWrite = (option = {}, { stripId = false } = {}) => {
  const payload = buildBidOptionPayload(option, null);
  delete payload.bid_id;
  // Options going through the nested-write endpoint are being attached
  // to a quote — stamp bid_option.status = 'SENT' to distinguish them
  // from pre-quote shortlist rows (which stay at 'DRAFT') and from the
  // client-picked winner ('ACCEPTED'). Preserve an existing ACCEPTED so
  // a post-payment quote edit doesn't downgrade the paid tail.
  payload.status = option.status === 'ACCEPTED' ? 'ACCEPTED' : 'SENT';
  if (stripId) return payload;
  const id = option.bid_option_id;
  if (id && !String(id).startsWith('new-')) {
    payload.bid_option_id = id;
  }
  return payload;
};

/**
 * Assemble the `quote.files` object embedded on the nested-write payload.
 * Mirrors what `patchQuoteFiles` used to write post-save: contract URL and
 * operator-name overrides. Returns undefined when nothing needs writing so
 * the payload stays lean.
 */
const buildFilesObject = (quoteData, uploadedContractPath) => {
  const files = {};
  const contractPath = uploadedContractPath || quoteData?.settings?.contractFile?.path || null;
  if (contractPath) files.contract = contractPath;
  const opOverrides = quoteData?.operatorNameOverrides || {};
  if (Object.keys(opOverrides).length > 0) {
    files.operator_name_overrides = { ...opOverrides };
  }
  return Object.keys(files).length > 0 ? files : undefined;
};

/**
 * Upload a freshly-picked contract file to S3 and return its path. Skips
 * when the user hasn't picked a new file (the settings.contractFile is
 * either absent, marked 'removed', or hydrated from a saved path with no
 * in-memory File). Used by both create (processQuote) and edit
 * (handleUpdateQuote / handleUpdateQuoteAndNotify) so a broker replacing
 * the contract in edit mode actually gets it persisted.
 *
 * Returns { contractPath: string | null } — contractPath === null when
 * nothing was uploaded, so callers know whether to patch `files.contract`.
 */
const uploadContractIfNeeded = async (quoteData, quoteId) => {
  const contractFile = quoteData?.settings?.contractFile?.file;
  if (!contractFile) return { contractPath: null };
  const key = `contracts/quotes/${quoteId || `Q${Date.now()}`}_${contractFile.name}`;
  const s3Urls = await apiClient.getS3SignedUrl(key, contractFile.type);
  const contractPath = await apiClient.uploadFileToS3(contractFile, s3Urls);
  return { contractPath };
};

/**
 * Hydrate any manually-added aircraft options into real BE records. Fires
 * at quote save/send time — the AddAircraftManuallyDrawer only stashes
 * form values + raw File objects in a session-local shortlist entry so
 * the broker isn't creating DB orphans if they navigate away.
 *
 * For each `_isManual` bid option:
 *   1. Upload each pending photo File to S3 via signed URL, collect the
 *      resulting absolute URLs.
 *   2. POST /aircrafts with the manual-drafted fields + S3 image URLs.
 *   3. Swap the placeholder `manual-...` id for the returned aircraft_id,
 *      hydrate the nested aircraft object, and clear the manual flags so
 *      the option looks identical to any other bidOption downstream.
 *
 * Options that aren't `_isManual` pass through unchanged. Throws on any
 * upload or create failure — the caller's outer try/catch surfaces the
 * error, no partial state persists (bid_options haven't been POSTed yet).
 */
const hydrateManualAircraft = async (bidOptions) => {
  if (!Array.isArray(bidOptions) || bidOptions.length === 0) return bidOptions;
  return Promise.all(
    bidOptions.map(async (opt) => {
      if (!opt?._isManual) return opt;

      const files = Array.isArray(opt._pendingPhotoFiles) ? opt._pendingPhotoFiles : [];
      const draft = opt._manualDraft || {};

      // 1) Upload photos in parallel. Key layout mirrors the BE-managed
      // static tree: photos/{tail_number}/{i}.{ext}. Extension is derived
      // from the file's MIME (jpeg/png) so the S3 URL displays with the
      // correct suffix.
      const tailFolder = String(draft.tail_number || 'unknown').replace(/[^A-Z0-9]/gi, '').toUpperCase() || 'UNKNOWN';
      const extFromMime = (type) =>
        type === 'image/png' ? 'png'
        : type === 'image/jpeg' || type === 'image/jpg' ? 'jpg'
        : (String(type || '').split('/')[1] || 'jpg');
      const uploadedUrls = await Promise.all(
        files.map(async (file, i) => {
          const ext = extFromMime(file.type);
          const key = `photos/${tailFolder}/${i}.${ext}`;
          const s3Urls = await apiClient.getS3SignedUrl(key, file.type);
          return apiClient.uploadFileToS3(file, s3Urls);
        }),
      );

      // 2) Create the aircraft record. Field names mirror what the manual
      // drawer collects — BE fills defaults / nulls for anything omitted.
      // operator_id comes from the SelectOperatorModal pick — required by
      // the BE to associate the aircraft with a real operator.
      const aircraftPayload = {
        tail_number: draft.tail_number,
        operator_id: draft.operator_id,
        make_model: draft.aircraft_name,
        manufacturer: draft.aircraft_name,
        passenger_capacity: opt.segment_pax_count || null,
        max_passengers: opt.segment_pax_count || null,
        description: draft.description || null,
        current_status: 'AVAILABLE',
        image_urls: uploadedUrls,
        banner_image_url: uploadedUrls[0] || null,
        aircraft_features: { custom_features: draft.amenities || [] },
      };
      const created = await apiClient.createAircraft(aircraftPayload);
      const realId = created?.aircraft_id;
      if (!realId) throw new Error('Aircraft was created but no aircraft_id was returned.');

      // 3) Merge back into the bidOption shape.
      return {
        ...opt,
        _isManual: undefined,
        _pendingPhotoFiles: undefined,
        _manualDraft: undefined,
        aircraft_id: realId,
        operator_id: created.operator_id || null,
        image_urls: uploadedUrls,
        banner_image_url: uploadedUrls[0] || null,
        aircraft: {
          ...(opt.aircraft || {}),
          ...created,
          aircraft_id: realId,
          image_urls: uploadedUrls,
          banner_image_url: uploadedUrls[0] || null,
        },
      };
    }),
  );
};

/**
 * Hook that handles all quote processing (draft saving & sending)
 */
const useQuoteProcessor = (organization = null) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { shortlistAircraft, selectedTripGroup } = useAppStore();
  const { userProfile } = useAuthStore();

  const [isSending, setIsSending] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [error, setError] = useState(null);

  // Resume state across retries within the same mount. The nested-write
  // endpoint is one atomic call, so a single guard suffices — if the write
  // succeeded, retry re-uses the response instead of racing another POST.
  // Reset to null on full success.
  const progressRef = useRef({ createdQuote: null });

  const processQuote = useCallback(async (quoteData, { status, send }) => {
    const brokerOrgId = userProfile?.org_id;
    const createdBy = userProfile?.userprofile_id;
    const finalPrice = quoteData.settings.final_price || 0;

    // 1) Persist per-aircraft custom_profile edits FIRST. Doing it before
    //    the nested write means the itinerary email's aircraft cards
    //    (which read aircraft.custom_profile via include=custom_profile)
    //    reflect the broker's edits on the same round trip.
    const profilesWritten = await persistAircraftProfiles(
      quoteData.aircraftProfiles,
      brokerOrgId,
    );

    // 2) Hydrate any manually-added aircraft (S3 upload + POST /aircrafts)
    //    so placeholder `manual-...` ids don't leak into the payload.
    const bidOptions = await hydrateManualAircraft(quoteData.bidOptions || []);

    // 3) Upload a freshly-picked contract PDF (if any) BEFORE the write —
    //    we embed its S3 URL inline in `quote.files.contract` so there's
    //    no separate patchQuoteFiles round trip. `Q<timestamp>` stands in
    //    for a real quote_id in the S3 key; keys never need to be
    //    round-trip resolvable.
    const { contractPath: uploadedContractPath } = await uploadContractIfNeeded(quoteData, null);

    // Route: Quick Quote when the shortlist has items; Trip Group when the
    // form is priced against an existing bid the shortlist drawer prepared.
    // Path 3 (edit mode) is handled by handleUpdateQuote and never enters
    // processQuote, so we don't check editQuoteId here.
    const isQuickQuote = Array.isArray(shortlistAircraft) && shortlistAircraft.length > 0;
    const tripGroupBidId = selectedTripGroup?.bids?.[0]?.bid_id;

    // Build the bid sub-object. In Quick Quote the bid is inserted; in
    // Trip Group the endpoint updates the already-persisted bid at
    // tripGroupBidId (identity fields — created_by, broker_org_id,
    // trip_number — are ignored on update, so leaving them in the payload
    // is harmless). Both paths flip bid.status → SENT so the source bid
    // drops out of the shortlist drawer's DRAFT-filtered query.
    const bidBase = isQuickQuote
      ? buildBidPayload(quoteData, { finalPrice, createdBy, brokerOrgId })
      : buildBidUpdatePayload(quoteData, finalPrice);

    // Kept options — every tail the broker still wants on this quote.
    // Trip Group already-persisted options → keep their id (endpoint
    // PATCHes them). Quick Quote / _isNew options → strip id (INSERT).
    const keptOptionsPayload = bidOptions.map((opt) =>
      buildBidOptionPayloadForNestedWrite(opt, {
        stripId: isQuickQuote || opt._isNew,
      }),
    );

    // Removed options — Trip Group only. `POST /quotes-with-bids`
    // deliberately does NOT diff-delete omitted options ("that trim is
    // PATCH-only" per the endpoint spec). But it DOES patch any option
    // we list explicitly by id. So instead of firing a separate
    // pre-flight `updateBidOption` PATCH for each removed row, we
    // include them in the same payload with `shortlisted:false +
    // status:'REJECTED'` — the endpoint patches them by id inside the
    // create transaction. Zero extra round trips.
    const removedOptionsPayload = (() => {
      if (isQuickQuote) return [];
      const sourceOptions = selectedTripGroup?.bids?.[0]?.bid_options || [];
      const keptIds = new Set(
        bidOptions
          .map((o) => o.bid_option_id)
          .filter((id) => id && !String(id).startsWith('new-')),
      );
      return sourceOptions
        .filter((o) => o?.bid_option_id && !keptIds.has(o.bid_option_id))
        .map((o) => ({
          bid_option_id: o.bid_option_id,
          shortlisted: false,
          status: 'REJECTED',
        }));
    })();

    const bid = {
      ...bidBase,
      ...(tripGroupBidId ? { bid_id: tripGroupBidId } : {}),
      status: 'SENT',
      bid_options: [...keptOptionsPayload, ...removedOptionsPayload],
    };

    // Build the quote sub-object. `buildQuoteCreatePayload` produces the
    // clean whitelist the API accepts; we layer the files object,
    // passengers (empty on fresh quotes; source-carry happens in the
    // duplicator), and the sent_by/sent_at audit stamps here so the
    // admin-visible Sent By row lands atomically.
    const quoteArgs = {
      brokerOrgId,
      createdBy,
      bidItems: isQuickQuote
        ? []  // BE creates the link via quote_bid_items when the bid inserts
        : (selectedTripGroup?.bids?.map((b) => ({ bid_id: b.bid_id })) || []),
      status,
      settings: quoteData.settings,
    };
    const quote = {
      ...buildQuoteCreatePayload(quoteData, quoteArgs),
      files: buildFilesObject(quoteData, uploadedContractPath),
      passengers: [],
    };
    if (send) {
      quote.sent_by = createdBy;
      quote.sent_at = new Date().toISOString();
    }

    // 4) One atomic nested write. Retry-idempotent via progressRef — if the
    //    POST succeeded but a later step (email dispatch, org branding
    //    update) failed, retrying resumes from that step instead of
    //    inserting a duplicate quote.
    if (!progressRef.current.createdQuote) {
      progressRef.current.createdQuote = await apiClient.createQuoteWithBids({
        quote,
        bid,
      });
    }
    const createdQuote = progressRef.current.createdQuote;

    // 5) Client email (send flow only). The nested-write response is
    //    already fully hydrated (bid_items → bid → bid_options → aircraft
    //    → custom_profile / operator), so no getQuoteById re-fetch.
    if (send) {
      await sendQuoteToClient(createdQuote, {
        quoteData,
        organization,
        userProfile,
      });
    }

    // 6) Admin contract-history carry-forward (best-effort). Uses the
    //    contractPath we just uploaded or the previously-hydrated path.
    const contractPath = uploadedContractPath || quoteData.settings?.contractFile?.path || null;
    if (contractPath && ['admin', 'ADMIN'].includes(userProfile?.role) && organization) {
      const existingHistory = organization?.branding?.contractHistory || [];
      await apiClient.updateOrganization(brokerOrgId, {
        branding: {
          ...organization?.branding,
          contractHistory: [...new Set([contractPath, ...existingHistory])],
        },
      });
    }

    // 7) Optimistically seed the quotes list cache off the server truth we
    //    just received (same shape as getQuoteById), so QuotesList renders
    //    instantly on nav. Background refetch reconciles shortly after.
    const optimisticQuote = {
      ...createdQuote,
      id: createdQuote?.quote_id || createdQuote?.id,
      status: createdQuote?.status || status,
      client: createdQuote?.client || quoteData.client?.clientData || {},
    };
    queryClient.setQueriesData(
      { predicate: (q) => q.queryKey[0] === 'quotes' && q.queryKey[1] === 'all' },
      (old) => [optimisticQuote, ...(old || [])],
    );
    // Seed the drawer cache so opening the details drawer for this quote
    // is instant instead of triggering a fresh getQuoteById.
    if (createdQuote?.quote_id) {
      queryClient.setQueryData(['quoteDetails', createdQuote.quote_id], createdQuote);
    }

    // 8) Cache invalidation. bid.status → SENT means the source bid drops
    //    out of the shortlist drawer's DRAFT-filtered query.
    queryClient.invalidateQueries({ queryKey: ['quotes'] });
    queryClient.invalidateQueries({ queryKey: ['shortlistedBids'] });
    if (profilesWritten) {
      queryClient.invalidateQueries({ queryKey: ['aircraft'] });
      queryClient.invalidateQueries({ queryKey: ['bids'] });
      queryClient.invalidateQueries({ queryKey: ['flights'] });
    }

    // Everything succeeded — clear resume state so the next quote starts fresh.
    progressRef.current = { createdQuote: null };

    toast.success(send ? 'Quote sent to client' : 'Draft saved');
    setTimeout(() => navigate(ROUTES.QUOTES.path), 500);
  }, [shortlistAircraft, selectedTripGroup, userProfile, queryClient, navigate, organization]);

  const handleSaveDraft = useCallback(async (quoteData) => {
    try {
      setIsSavingDraft(true);
      setError(null);
      // A regular Save Draft is the JB's private working state — status stays
      // DRAFT for everyone. Submitting for admin review is a separate action
      // (see handleSubmitForApproval below).
      await processQuote(quoteData, { status: 'DRAFT', send: false });
    } catch (err) {
      setError(err.message || 'Failed to save draft. Please try again.');
      setIsSavingDraft(false);
    }
  }, [processQuote]);

  // Junior Broker "Submit for Approval" — status → PENDING_APPROVAL, no
  // client email, shows up in Admin's Pending Approvals list.
  const handleSubmitForApproval = useCallback(async (quoteData) => {
    try {
      setIsSavingDraft(true);
      setError(null);
      await processQuote(quoteData, { status: 'PENDING_APPROVAL', send: false });
    } catch (err) {
      setError(err.message || 'Failed to submit for approval. Please try again.');
      setIsSavingDraft(false);
    }
  }, [processQuote]);

  const handleSendQuote = useCallback(async (quoteData) => {
    try {
      setIsSending(true);
      setError(null);
      await processQuote(quoteData, { status: 'SENT', send: true });
    } catch (err) {
      console.error('sendQuote error:', err);
      setError(err.message || 'Failed to process quote. Please try again.');
      setIsSending(false);
    }
  }, [processQuote]);

  /**
   * Edit-mode submit. Fires on Save Changes in `GenerateQuote` when the
   * form was hydrated via `?quoteid=`. One atomic call:
   *   PATCH /quotes-with-bids/:quote_id  (bid trip fields + bid_options
   *   diff + quote patch + quote.files merge, all in one transaction).
   *
   * bid_options semantics: we pass the FULL desired-set. Options with a
   * real id → PATCH; new/`_isNew` → INSERT; options omitted from the array
   * → soft-unshortlisted by the endpoint (replaces the old
   * `reconcileBidOptions` soft-delete branch). Never pass a sparse list.
   *
   * Throws on failure so the caller can surface the error itself.
   */
  const handleUpdateQuote = useCallback(async (quoteData, opts = {}) => {
    const { targetStatus } = opts;
    const editing = quoteData?._editing;
    if (!editing?.quote_id) {
      setError('Edit mode requires an existing quote_id');
      return;
    }
    try {
      setIsSending(true);
      setError(null);

      // 1) Persist per-aircraft custom_profile edits from the drawer.
      const profilesWritten = await persistAircraftProfiles(
        quoteData.aircraftProfiles,
        userProfile?.org_id,
      );

      // 2) Hydrate any manually-added aircraft so placeholder `manual-...`
      //    ids don't leak into the payload.
      const bidOptions = await hydrateManualAircraft(quoteData.bidOptions || []);

      // 3) Upload a freshly-picked contract PDF (broker replaced it during
      //    edit). Embedded inline in `quote.files` on the PATCH — no
      //    separate patchQuoteFiles round trip.
      const { contractPath } = await uploadContractIfNeeded(quoteData, editing.quote_id);

      // 4) Build the atomic PATCH payload.
      //    Kept options: PATCH by id (or INSERT if _isNew).
      //    Removed options: explicitly list with {shortlisted:false,
      //      status:'REJECTED'} so the endpoint stamps BOTH fields —
      //      relying on the endpoint's auto-soft-delete for omitted
      //      options only guarantees shortlisted:false, leaving status
      //      column potentially at 'DRAFT' and breaking the lockstep
      //      invariant analytics keys on.
      const keptOptionsPayload = bidOptions.map((opt) =>
        buildBidOptionPayloadForNestedWrite(opt, { stripId: !!opt._isNew }),
      );
      const keptIds = new Set(
        bidOptions
          .map((o) => o.bid_option_id)
          .filter((id) => id && !String(id).startsWith('new-')),
      );
      const removedOptionsPayload = (editing.bid_option_ids || [])
        .filter((id) => id && !keptIds.has(id))
        .map((id) => ({
          bid_option_id: id,
          shortlisted: false,
          status: 'REJECTED',
        }));

      const bid = editing.bid_id
        ? {
            bid_id: editing.bid_id,
            ...buildBidEditPayload(quoteData),
            bid_options: [...keptOptionsPayload, ...removedOptionsPayload],
          }
        : undefined;
      const quote = buildQuotePatchPayload(quoteData);
      if (targetStatus) quote.status = targetStatus;
      const files = buildFilesObject(quoteData, contractPath);
      if (files) quote.files = files;

      const updatedQuote = await apiClient.updateQuoteWithBids(
        editing.quote_id,
        bid ? { quote, bid } : { quote },
      );

      // 5) Cache seeds off the fresh server truth.
      queryClient.setQueryData(['quoteDetails', editing.quote_id], updatedQuote);

      // 6) Cache invalidation.
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quoteDetails', editing.quote_id] });
      queryClient.invalidateQueries({ queryKey: ['shortlistedBids'] });
      if (profilesWritten) {
        queryClient.invalidateQueries({ queryKey: ['aircraft'] });
        queryClient.invalidateQueries({ queryKey: ['bids'] });
        queryClient.invalidateQueries({ queryKey: ['flights'] });
      }
      queryClient.invalidateQueries({ queryKey: ['tax-reports'] });

      toast.success(targetStatus === 'PENDING_APPROVAL' ? 'Submitted for approval' : 'Quote updated');
      setTimeout(() => navigate(ROUTES.QUOTES.path), 500);
    } catch (err) {
      console.error('updateQuote error:', err);
      setError(err.message || 'Failed to save changes. Please try again.');
      toast.error('Failed to save changes');
    } finally {
      setIsSending(false);
    }
  }, [queryClient, navigate, userProfile]);

  /**
   * Edit-mode submit + email notify. Same atomic PATCH as
   * `handleUpdateQuote`, then sends an updated proposal email to the
   * client using the PATCH response (no re-fetch).
   */
  const handleUpdateQuoteAndNotify = useCallback(async (quoteData) => {
    const editing = quoteData?._editing;
    if (!editing?.quote_id) {
      setError('Edit mode requires an existing quote_id');
      return;
    }
    try {
      setIsSending(true);
      setError(null);

      const profilesWritten = await persistAircraftProfiles(
        quoteData.aircraftProfiles,
        userProfile?.org_id,
      );

      // Both DRAFT and PENDING_APPROVAL transition to SENT on first
      // send-to-client. Later statuses (VIEWED / SIGNED / ACCEPTED) are
      // preserved so a resend doesn't roll the client's progress back.
      const isFirstSend =
        editing.source_status === 'DRAFT' ||
        editing.source_status === 'PENDING_APPROVAL';

      const bidOptions = await hydrateManualAircraft(quoteData.bidOptions || []);
      const { contractPath } = await uploadContractIfNeeded(quoteData, editing.quote_id);

      // Build the atomic PATCH payload — same inline-removed-options
      // pattern as handleUpdateQuote so lockstep (shortlisted ⇔ status)
      // holds without depending on BE auto-soft-delete stamping both.
      const bidBase = buildBidEditPayload(quoteData);
      if (isFirstSend) bidBase.status = 'SENT';
      const keptOptionsPayload = bidOptions.map((opt) =>
        buildBidOptionPayloadForNestedWrite(opt, { stripId: !!opt._isNew }),
      );
      const keptIds = new Set(
        bidOptions
          .map((o) => o.bid_option_id)
          .filter((id) => id && !String(id).startsWith('new-')),
      );
      const removedOptionsPayload = (editing.bid_option_ids || [])
        .filter((id) => id && !keptIds.has(id))
        .map((id) => ({
          bid_option_id: id,
          shortlisted: false,
          status: 'REJECTED',
        }));
      const bid = editing.bid_id
        ? {
            bid_id: editing.bid_id,
            ...bidBase,
            bid_options: [...keptOptionsPayload, ...removedOptionsPayload],
          }
        : undefined;

      const quote = buildQuotePatchPayload(quoteData);
      if (isFirstSend) {
        quote.status = 'SENT';
        quote.sent_by = userProfile?.userprofile_id;
        quote.sent_at = new Date().toISOString();
      }

      // Files object — merge in any admin-approval audit stamp when the
      // source was PENDING_APPROVAL, alongside the contract path / op-name
      // overrides. `quote.files` is merge-safe on this endpoint (BE
      // preserves sibling keys), so the tripsheet URL, contract_signed,
      // crews etc. survive.
      const files = buildFilesObject(quoteData, contractPath) || {};
      if (editing.source_status === 'PENDING_APPROVAL') {
        files.approval = {
          approved_by: userProfile?.userprofile_id,
          approved_at: new Date().toISOString(),
        };
      }
      if (Object.keys(files).length > 0) quote.files = files;

      const updatedQuote = await apiClient.updateQuoteWithBids(
        editing.quote_id,
        bid ? { quote, bid } : { quote },
      );

      // Send the client notification email off the PATCH response — no
      // getQuoteById re-fetch, the response already carries the full
      // include tree the email template needs.
      await notifyClientOfQuoteUpdate(updatedQuote, {
        quoteData,
        organization,
        userProfile,
      });

      queryClient.setQueryData(['quoteDetails', editing.quote_id], updatedQuote);

      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quoteDetails', editing.quote_id] });
      queryClient.invalidateQueries({ queryKey: ['tax-reports'] });
      queryClient.invalidateQueries({ queryKey: ['shortlistedBids'] });
      if (profilesWritten) {
        queryClient.invalidateQueries({ queryKey: ['aircraft'] });
        queryClient.invalidateQueries({ queryKey: ['bids'] });
        queryClient.invalidateQueries({ queryKey: ['flights'] });
      }

      toast.success('Quote updated and email sent');
      setTimeout(() => navigate(ROUTES.QUOTES.path), 500);
    } catch (err) {
      console.error('updateQuoteAndNotify error:', err);
      setError(err.message || 'Failed to save changes. Please try again.');
      toast.error('Failed to save changes');
    } finally {
      setIsSending(false);
    }
  }, [queryClient, navigate, organization, userProfile]);

  return {
    handleSaveDraft,
    handleSubmitForApproval,
    handleSendQuote,
    handleUpdateQuote,
    handleUpdateQuoteAndNotify,
    isSending,
    isSavingDraft,
    error,
    setError,
  };
};

export default useQuoteProcessor;
